import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import Map, { Marker, Source } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useMsal } from '@azure/msal-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import StatsRow from './components/StatsRow';
import LiveFeed from './components/LiveFeed';
import { loginRequest, graphRequest } from './authConfig';
import { fetchMicrosoftLicenseData } from './licenseData';
import LocationPreviewPanel from './components/locations/LocationPreviewPanel';

// Fix for "Failed to initialize WebGL" in CRA/Dev
// Forces Mapbox to load the worker from CDN instead of the potentially broken local bundle


const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Use relative paths for API calls.
const api = axios.create({
    baseURL: ''
});

const launchpadGlassCardStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(64px) saturate(200%)',
    WebkitBackdropFilter: 'blur(64px) saturate(200%)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.16), inset 0 -1px 0 rgba(255, 255, 255, 0.04), 0 30px 86px rgba(0, 0, 0, 0.44)',
};

const overviewGlassCardStyles = `
    .overview-launchpad-card:hover,
    .overview-launchpad-card:focus-visible {
        border-color: rgba(255, 255, 255, 0.18) !important;
        background-color: rgba(255, 255, 255, 0.045) !important;
    }
`;

// --- Mapbox Marker Colors ---
const PIN_COLORS = {
    critical: '#ef4444',
    warning: '#fbbf24',
    healthy: '#22c55e'
};

// --- Get pin color based on status ---
const getPinColor = (hasCritical, hasNeedsAttention) => {
    if (hasCritical) return PIN_COLORS.critical;
    if (hasNeedsAttention) return PIN_COLORS.warning;
    return PIN_COLORS.healthy;
};

// --- AISummaryModal (Unchanged) ---
const AISummaryModal = ({ onClose, onGenerate, summary, error, isLoading }) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        requestAnimationFrame(() => setIsVisible(true));
    }, []);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(onClose, 300);
    };

    return (
        <div
            className={`fixed inset - 0 z - 50 flex items - center justify - center transition - all duration - 300 ease - out ${isVisible ? 'backdrop-blur-md bg-black/60' : 'backdrop-blur-none bg-black/0'} `}
            onClick={handleClose}
        >
            <div
                className={`
                    relative w - full max - w - 3xl m - 4 flex flex - col
bg - [#1a1a1a] / 90 border border - white / 10 rounded - 2xl shadow - 2xl overflow - hidden
transition - all duration - 300 cubic - bezier(0.34, 1.56, 0.64, 1)
                    ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'}
`}
                style={{ maxHeight: '85vh' }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex-shrink-0 flex justify-between items-center p-6 bg-[#0f0f0f]/50 relative">
                    <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                    <h2 className="text-2xl font-bold text-white flex items-center tracking-tight">
                        <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-red-600/10 mr-4 border border-red-600/20">
                            <svg className="h-6 w-6 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM18 12.75l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 18l-1.035.259a3.375 3.375 0 00-2.455-2.456L14.25 18l1.036-.259a3.375 3.375 0 002.455-2.456L18 12.75z" />
                            </svg>
                        </span>
                        TCB AI Summary
                    </h2>
                    <button type="button" onClick={handleClose} className="group p-2 rounded-full hover:bg-white/5 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-400 group-hover:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
                {/* Content */}
                <div className="flex-grow overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent custom-markdown">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <div className="relative w-16 h-16 mb-6">
                                <div className="absolute inset-0 rounded-full border-t-2 border-red-500 animate-spin"></div>
                                <div className="absolute inset-2 rounded-full border-t-2 border-blue-500 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
                            </div>
                            <p className="text-gray-300 font-medium">Analyzing system data...</p>
                            <p className="text-gray-500 text-sm mt-1">This may take a moment</p>
                        </div>
                    ) : error ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
                            </div>
                            <h3 className="text-lg font-semibold text-white mb-2">Analysis Failed</h3>
                            <p className="text-gray-400 max-w-xs mx-auto">{error}</p>
                        </div>
                    ) : (
                        <article className="prose prose-invert prose-headings:text-white prose-p:text-gray-300 prose-li:text-gray-300 prose-strong:text-red-400 max-w-none">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{summary}</ReactMarkdown>
                        </article>
                    )}
                </div>
                {/* Footer */}
                <div className="flex-shrink-0 p-6 bg-[#0f0f0f]/50 flex justify-end gap-3 border-t border-white/5">
                    <button type="button" onClick={onGenerate} disabled={isLoading} className="px-5 py-2.5 text-sm font-medium text-gray-300 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                        {isLoading ? 'Processing...' : 'Regenerate'}
                    </button>
                    <button type="button" onClick={handleClose} className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-br from-red-600 to-red-700 rounded-xl hover:shadow-lg hover:shadow-red-900/40 transition-all hover:-translate-y-0.5">
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};





export default function DashboardPage({ isPreviewMode = false }) {
    const { instance, inProgress } = useMsal();
    const mapRef = useRef(null);
    const isFetching = useRef(false);
    const [issues, setIssues] = useState([]);
    const [mapLocations, setMapLocations] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState('All Locations');
    const [previewLocation, setPreviewLocation] = useState(null);
    const [previewTicketCount, setPreviewTicketCount] = useState(null);

    // --- NEW: AI State ---
    const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
    const [isSummaryLoading, setIsSummaryLoading] = useState(false);
    const [summaryContent, setSummaryContent] = useState("");
    const [summaryError, setSummaryError] = useState("");

    // --- Map Style Toggle ---
    const [mapStyle, setMapStyle] = useState('dark'); // 'dark' or 'satellite'
    const [radarOn, setRadarOn] = useState(true);
    const [isMapReady, setIsMapReady] = useState(false);
    const [radarTimeline, setRadarTimeline] = useState({ frames: [], pastFrameCount: 0 }); // all past + forecast frames
    const [radarFrameIndex, setRadarFrameIndex] = useState(0);
    const [currentBearing, setCurrentBearing] = useState(0); // Track map bearing for compass
    const radarFrames = radarTimeline.frames;
    const pastFrameCount = radarTimeline.pastFrameCount;
    const hasForecastFrames = radarFrames.length > pastFrameCount;
    const isForecastFrame = hasForecastFrames && radarFrameIndex >= pastFrameCount;
    const timelineBoundaryPercent = radarFrames.length > 0 ? (pastFrameCount / radarFrames.length) * 100 : 0;
    const MAP_STYLES = {
        dark: 'mapbox://styles/mapbox/dark-v11',
        satellite: 'mapbox://styles/mapbox/satellite-streets-v12'
    };

    const locationsForDropdown = useMemo(() => {
        const uniqueLocations = [...new Set(mapLocations.map(loc => loc.boardName).filter(name => name && name.toLowerCase().includes("walk-on")))];
        return uniqueLocations.sort();
    }, [mapLocations]);

    const getAuthToken = useCallback(async () => {
        const accounts = instance.getAllAccounts();
        if (accounts.length === 0) {
            throw new Error("No accounts found");
        }
        const response = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
        return response.accessToken;
    }, [instance]);

    const handleLogout = useCallback(() => {
        if (isPreviewMode) return;
        instance.logoutRedirect({ postLogoutRedirectUri: "/" });
    }, [instance, isPreviewMode]);

    const fetchData = useCallback(async () => {
        if (inProgress !== "none" || isFetching.current) return;
        isFetching.current = true;

        try {
            const token = await getAuthToken();

            const apiHeaders = { headers: { 'x-auth-token': token } };

            const [issuesRes, locationsRes] = await Promise.all([
                api.get('/api/issues', apiHeaders),
                api.get('/api/locations', apiHeaders)
            ]);

            setIssues(issuesRes.data);
            setMapLocations(locationsRes.data);

        } catch (err) {
            const isAuthorizationError = err.response && (err.response.status === 401 || err.response.status === 403);
            if (!(isPreviewMode && isAuthorizationError)) {
                console.error('Fetch error:', err);
            }
            if (isAuthorizationError) {
                setTimeout(handleLogout, 3000);
            } else if (err.name === "InteractionRequiredAuthError" && !isPreviewMode) {
                instance.acquireTokenRedirect(loginRequest);
            }
        } finally {
            if (inProgress === "none") {
                isFetching.current = false;
            }
        }
    }, [getAuthToken, handleLogout, inProgress, instance, isPreviewMode]);

    const handleGenerateSummary = useCallback(async () => {
        setIsSummaryModalOpen(true);
        setIsSummaryLoading(true);
        setSummaryError("");
        try {
            const token = await getAuthToken();
            const apiHeaders = { headers: { 'x-auth-token': token } };
            const res = await api.get('/api/ai/summarize-issues', apiHeaders);
            setSummaryContent(res.data.summary);
        } catch (err) {
            console.error("Error generating summary:", err);
            setSummaryError("We apologize, the TCB AI system is currently unavailable. Please try again later or contact support for assistance.");
            setSummaryContent("");
        } finally {
            setIsSummaryLoading(false);
        }
    }, [getAuthToken]);

    useEffect(() => {
        if (isPreviewMode) return undefined;

        fetchData();
        const interval = setInterval(fetchData, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [fetchData, isPreviewMode]);

    useEffect(() => {
        if (isPreviewMode) return undefined;

        let isMounted = true;

        const loadSupportSummary = async () => {
            if (!previewLocation?.locationId) {
                setPreviewTicketCount(null);
                return;
            }

            setPreviewTicketCount(null);
            try {
                const token = await getAuthToken();
                const response = await api.get(
                    `/api/locations/profiles/${previewLocation.locationId}/support-summary`,
                    { headers: { 'x-auth-token': token } }
                );
                if (!isMounted) return;

                const activeTicketCount = response.data?.ticketSummary?.activeTicketCount;
                setPreviewTicketCount(Number.isFinite(activeTicketCount) ? activeTicketCount : null);
            } catch (error) {
                if (isMounted) setPreviewTicketCount(null);
            }
        };

        loadSupportSummary();

        return () => {
            isMounted = false;
        };
    }, [getAuthToken, isPreviewMode, previewLocation?.locationId]);

    // Listen for logo-click refresh event from Layout
    useEffect(() => {
        const handleRefresh = () => fetchData();
        window.addEventListener('dashboard-refresh', handleRefresh);
        return () => window.removeEventListener('dashboard-refresh', handleRefresh);
    }, [fetchData]);

    // --- Fetch RainViewer radar frames (past + forecast) ---
    useEffect(() => {
        if (!radarOn || isPreviewMode) return;
        const fetchRadar = async () => {
            try {
                const res = await fetch('https://api.rainviewer.com/public/weather-maps.json');
                const data = await res.json();
                const past = data.radar?.past || [];
                const nowcast = data.radar?.nowcast || [];
                const allFrames = [...past, ...nowcast];
                if (allFrames.length > 0) {
                    setRadarTimeline({ frames: allFrames, pastFrameCount: past.length });
                    setRadarFrameIndex(Math.max(past.length - 1, 0)); // start at "now"
                }
            } catch (err) {
                console.error('Failed to fetch radar data:', err);
            }
        };
        fetchRadar();
        const refreshInterval = setInterval(fetchRadar, 5 * 60 * 1000);
        return () => clearInterval(refreshInterval);
    }, [isPreviewMode, radarOn]);

    // --- Pre-load ALL radar frames as separate Mapbox layers for glass-smooth playback ---
    const radarLayersRef = useRef([]);

    useEffect(() => {
        const map = mapRef.current?.getMap?.();
        if (!isMapReady || !map || !radarOn || radarFrames.length === 0) return;

        // Wait for map style to be loaded
        const addLayers = () => {
            // Clean up any previously added radar layers
            radarLayersRef.current.forEach(id => {
                if (map.getLayer(id)) map.removeLayer(id);
                if (map.getSource(id)) map.removeSource(id);
            });
            radarLayersRef.current = [];

            // Add ALL frames as separate sources + layers (tiles pre-cache in GPU)
            radarFrames.forEach((frame, i) => {
                const srcId = `rv-radar-${i}`;
                const tileUrl = `https://tilecache.rainviewer.com${frame.path}/256/{z}/{x}/{y}/6/1_1.png`;

                if (!map.getSource(srcId)) {
                    map.addSource(srcId, {
                        type: 'raster',
                        tiles: [tileUrl],
                        tileSize: 256,
                        maxzoom: 6
                    });
                }
                if (!map.getLayer(srcId)) {
                    map.addLayer({
                        id: srcId,
                        type: 'raster',
                        source: srcId,
                        paint: {
                            'raster-opacity': 0,
                            'raster-fade-duration': 0
                        }
                    });
                }
                radarLayersRef.current.push(srcId);
            });

            // Show the current frame
            const currentId = `rv-radar-${radarFrameIndex}`;
            if (map.getLayer(currentId)) {
                map.setPaintProperty(currentId, 'raster-opacity', 0.7);
            }
        };

        if (map.isStyleLoaded()) {
            addLayers();
        } else {
            map.once('style.load', addLayers);
        }

        return () => {
            // Cleanup when radar turns off or frames change
            radarLayersRef.current.forEach(id => {
                if (map.getLayer(id)) map.removeLayer(id);
                if (map.getSource(id)) map.removeSource(id);
            });
            radarLayersRef.current = [];
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMapReady, mapStyle, radarOn, radarFrames]);

    // --- Animate: toggle opacity between pre-loaded layers ---
    const prevFrameRef = useRef(null);
    useEffect(() => {
        const map = mapRef.current?.getMap?.();
        if (!isMapReady || !map || !radarOn || radarFrames.length === 0) return;

        // Hide previous frame
        if (prevFrameRef.current !== null) {
            const prevId = `rv-radar-${prevFrameRef.current}`;
            if (map.getLayer(prevId)) {
                map.setPaintProperty(prevId, 'raster-opacity', 0);
            }
        }
        // Show current frame
        const currentId = `rv-radar-${radarFrameIndex}`;
        if (map.getLayer(currentId)) {
            map.setPaintProperty(currentId, 'raster-opacity', 0.7);
        }
        prevFrameRef.current = radarFrameIndex;
    }, [isMapReady, radarFrameIndex, radarOn, radarFrames]);

    // --- Animate radar loop timer ---
    useEffect(() => {
        if (!radarOn || radarFrames.length === 0) return;
        const animate = setInterval(() => {
            setRadarFrameIndex(prev => {
                const next = prev + 1;
                return next >= radarFrames.length ? 0 : next;
            });
        }, 800);
        return () => clearInterval(animate);
    }, [radarOn, radarFrames]);

    // Calculate Stats for HUD
    const [licensedUsers, setLicensedUsers] = useState(null);
    const [paidSeats, setPaidSeats] = useState(null);

    // Fetch M365 licensed users count + paid seats from Microsoft Graph
    useEffect(() => {
        if (isPreviewMode) return;

        const fetchLicenseData = async () => {
            try {
                const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
                if (!account) return;

                const tokenResponse = await instance.acquireTokenSilent({
                    ...graphRequest,
                    account
                });

                const licenseData = await fetchMicrosoftLicenseData({
                    accessToken: tokenResponse.accessToken
                });
                setLicensedUsers(licenseData.licensedUsers);
                setPaidSeats(licenseData.paidSeats);

            } catch (err) {
                console.error('Failed to fetch license data:', err);
            }
        };
        fetchLicenseData();
    }, [instance, isPreviewMode]);

    const stats = useMemo(() => {
        return {
            totalIssues: issues.length,
            critical: issues.filter(i => i.status === 'Critical').length,
            licensedUsers: licensedUsers,
            paidSeats: paidSeats,
            uptime: '99.98%' // Placeholder for now
        };
    }, [issues, licensedUsers, paidSeats]);

    return (
        <div className="flex flex-col h-full overflow-hidden animate-fadeInUp px-3 pt-3" style={{ background: '#000000', backgroundColor: '#000000' }}>
            <style>{overviewGlassCardStyles}</style>
            {/* HUD / Stats Row */}
            <StatsRow stats={stats} />

            {/* Split Pane Bento Grid */}
            <div className="flex-1 overflow-y-auto lg:overflow-hidden min-h-0 pb-6 pointer-events-auto">
                <div className="flex flex-col lg:grid lg:grid-cols-12 gap-6 h-auto lg:h-full">

                    {/* 1. MAP TILE (Left, 8 cols) */}
                    <div className="overview-launchpad-card relative lg:col-span-8 h-[50vh] min-h-[400px] lg:h-full lg:min-h-0 rounded-3xl overflow-hidden group" style={launchpadGlassCardStyle}>

                        {/* Map Control Buttons */}
                        <div className="absolute top-4 right-4 z-[400] flex items-center gap-2">
                            {/* Radar Toggle */}
                            <button
                                onClick={() => setRadarOn(!radarOn)}
                                className={`flex items-center gap-2 px-3 py-1.5 border rounded-full text-xs font-medium backdrop-blur-sm transition-all duration-300 ${
                                    radarOn
                                        ? 'bg-sky-500/20 border-sky-400/40 text-sky-300 shadow-[0_0_12px_rgba(56,189,248,0.15)]'
                                        : 'bg-black/60 border-white/10 text-gray-300 hover:bg-black/80 hover:border-white/20'
                                }`}
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
                                </svg>
                                Radar
                            </button>
                            {/* View Style Toggle */}
                            <button
                                onClick={() => setMapStyle(mapStyle === 'dark' ? 'satellite' : 'dark')}
                                className="flex items-center gap-2 px-3 py-1.5 bg-black/60 border border-white/10 rounded-full text-xs text-gray-300 font-medium backdrop-blur-sm hover:bg-black/80 hover:border-white/20 transition-all duration-200"
                            >
                                {mapStyle === 'satellite' ? 'Dark Mode' : 'Satellite'}
                            </button>
                        </div>

                        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'auto' }}>
                            <Map
                                ref={mapRef}
                                mapboxAccessToken={MAPBOX_TOKEN}
                                reuseMaps={true}
                                initialViewState={{
                                    longitude: -96.5,
                                    latitude: 31.5,
                                    zoom: 5.5,
                                    pitch: 0,
                                    bearing: 0
                                }}
                                onMove={(evt) => setCurrentBearing(evt.viewState.bearing)}
                                style={{ width: '100%', height: '100%' }}
                                mapStyle={MAP_STYLES[mapStyle]}
                                attributionControl={false}
                                terrain={{ source: 'mapbox-dem', exaggeration: 2.0 }}
                                logoPosition="bottom-left"
                                onLoad={() => {
                                    // Hide Mapbox logo
                                    const logo = document.querySelector('.mapboxgl-ctrl-logo');
                                    if (logo) logo.style.display = 'none';
                                    setIsMapReady(true);
                                }}
                            >
                                <Source
                                    id="mapbox-dem"
                                    type="raster-dem"
                                    url="mapbox://mapbox.mapbox-terrain-dem-v1"
                                    tileSize={512}
                                    maxzoom={14}
                                />

                                {/* Radar layer managed natively via useEffect above */}

                                {/* Custom Navigation Controls */}
                                <div className="absolute bottom-6 right-6 flex flex-col gap-3 z-[400]">
                                    <button
                                        onClick={() => mapRef.current?.zoomIn()}
                                        className="w-8 h-8 flex items-center justify-center bg-black/60 border border-white/10 rounded-lg text-white/80 hover:bg-black/80 hover:text-white hover:border-white/30 backdrop-blur-md transition-all shadow-lg active:scale-95"
                                        title="Zoom In"
                                    >
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => mapRef.current?.zoomOut()}
                                        className="w-8 h-8 flex items-center justify-center bg-black/60 border border-white/10 rounded-lg text-white/80 hover:bg-black/80 hover:text-white hover:border-white/30 backdrop-blur-md transition-all shadow-lg active:scale-95"
                                        title="Zoom Out"
                                    >
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => mapRef.current?.flyTo({ pitch: 0, bearing: 0, duration: 1500 })}
                                        className="w-8 h-8 flex items-center justify-center bg-black/60 border border-white/10 rounded-lg text-white/80 hover:bg-black/80 hover:text-white hover:border-white/30 backdrop-blur-md transition-all shadow-lg group active:scale-95"
                                        title="Reset North"
                                    >
                                        <div
                                            className="relative w-5 h-5 flex items-center justify-center transition-transform duration-100 ease-linear"
                                            style={{ transform: `rotate(${- currentBearing}deg)` }}
                                        >
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M12 2L4.5 20.29C4.1 21.27 5.25 22.18 6.09 21.46L12 16.5l5.91 4.96c.84.72 1.99-.19 1.59-1.17L12 2z" />
                                            </svg>
                                        </div>
                                    </button>
                                </div>

                                {/* Radar Legend + Timeline */}
                                {radarOn && (
                                    <div
                                        data-testid="radar-severity-legend"
                                        className="absolute bottom-6 left-6 z-[400] w-56 bg-black/70 backdrop-blur-sm border border-white/10 rounded-xl px-3 py-2.5 flex flex-col gap-2"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse" />
                                                <span className="text-[9px] text-white/50 font-semibold uppercase tracking-widest">
                                                    {isForecastFrame ? 'Forecast' : 'Live Radar'}
                                                </span>
                                            </div>
                                            {radarFrames.length > 0 && radarFrames[radarFrameIndex] && (
                                                <span className={`text-[9px] font-mono ${isForecastFrame ? 'text-amber-400/60' : 'text-white/40'}`}>
                                                    {new Date(radarFrames[radarFrameIndex].time * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            )}
                                        </div>
                                        {/* Timeline with NOW marker */}
                                        {radarFrames.length > 1 && (
                                            <div className="relative">
                                                <div className="flex items-center gap-0.5 mb-1">
                                                    <span className="text-[7px] text-white/20 uppercase">Past</span>
                                                    <div className="flex-1" />
                                                    <span className="text-[7px] text-white/20 uppercase">{hasForecastFrames ? 'Forecast' : 'Current'}</span>
                                                </div>
                                                <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden relative">
                                                    {/* Past portion */}
                                                    <div
                                                        className="absolute left-0 top-0 h-full bg-sky-400/50 rounded-full"
                                                        style={{ width: `${timelineBoundaryPercent}%` }}
                                                    />
                                                    {/* Forecast portion */}
                                                    {hasForecastFrames && (
                                                        <div
                                                            className="absolute top-0 h-full bg-amber-400/30 rounded-r-full"
                                                            style={{ left: `${timelineBoundaryPercent}%`, width: `${100 - timelineBoundaryPercent}%` }}
                                                        />
                                                    )}
                                                    {/* Playhead */}
                                                    <div
                                                        className="absolute top-[-1px] w-1 h-[calc(100%+2px)] bg-white rounded-full shadow-[0_0_4px_rgba(255,255,255,0.6)] transition-all duration-700 ease-linear"
                                                        style={{ left: `${(radarFrameIndex / (radarFrames.length - 1)) * 100}%` }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        <div className="w-full">
                                            <div
                                                data-testid="radar-severity-spectrum"
                                                className="h-2.5 w-full rounded-full"
                                                style={{ background: 'linear-gradient(90deg, #00ff00 0%, #00cc00 20%, #ffff00 40%, #ff8800 60%, #ff0000 80%, #cc0066 100%)' }}
                                            />
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-[8px] text-white/30">Light</span>
                                            <span className="text-[8px] text-white/30">Moderate</span>
                                            <span className="text-[8px] text-white/30">Severe</span>
                                        </div>
                                    </div>
                                )}

                                {mapLocations.map((loc) => {
                                    const locationIssues = issues.filter(issue => issue.boardName === loc.boardName);
                                    const hasCritical = locationIssues.some(issue => issue.status === 'Critical');
                                    const hasNeedsAttention = locationIssues.some(issue => issue.status === 'Needs Attention');

                                    return (
                                        <Marker
                                            key={loc.boardName}
                                            longitude={loc.lng}
                                            latitude={loc.lat}
                                            anchor="center"
                                            onClick={(event) => {
                                                event.originalEvent.stopPropagation();
                                                setSelectedLocation(loc.boardName);
                                                setPreviewLocation(loc);
                                            }}
                                        >
                                            <div
                                                className="map-pin-diode cursor-pointer transition-transform duration-200 hover:scale-125"
                                                style={{ backgroundColor: getPinColor(hasCritical, hasNeedsAttention) }}
                                            />
                                        </Marker>
                                    );
                                })}
                            </Map>
                        </div>
                        {previewLocation && (
                            <LocationPreviewPanel
                                location={previewLocation}
                                issues={issues.filter(issue => issue.boardName === previewLocation.boardName)}
                                ticketCount={previewTicketCount}
                                onClose={() => setPreviewLocation(null)}
                            />
                        )}
                    </div>

                    {/* 2. ACTIVITY FEED TILE (Right, 4 cols) */}
                    <div className="lg:col-span-4 h-full">
                        <LiveFeed
                            issues={issues}
                            selectedLocation={selectedLocation}
                            setSelectedLocation={setSelectedLocation}
                            locations={locationsForDropdown}
                            onIssueClick={(boardName) => {
                                const location = mapLocations.find(loc => loc.boardName === boardName);
                                if (location && mapRef.current) {
                                    mapRef.current.flyTo({
                                        center: [location.lng, location.lat],
                                        zoom: 14,
                                        pitch: 50,
                                        duration: 2000,
                                        essential: true
                                    });
                                }
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* AI Floating Button removed for now */}

            {isSummaryModalOpen && (
                <AISummaryModal
                    onClose={() => setIsSummaryModalOpen(false)}
                    onGenerate={handleGenerateSummary}
                    summary={summaryContent}
                    error={summaryError}
                    isLoading={isSummaryLoading}
                />
            )}
        </div>
    );
}
